import { z } from "zod";
import { type SpApiOAuthResultMessage, spApiOAuthResultMessageSchema } from "./sp-api-oauth";

export const SP_API_OAUTH_TIMEOUT_MS = 10 * 60 * 1000;
const STORAGE_KEY = "adpilot:sp-api-popup";
const ACK = "sp-api-result-received";
const relaySchema = z.object({ id: z.string().uuid(), expiresAt: z.number() });
type RelayStorage = Pick<Storage, "setItem" | "getItem" | "removeItem">;

/** The popup retains its tab's sessionStorage even if an OAuth hop severs window.opener. */
export function receiveSpApiOAuthResult(
  popupStorage: RelayStorage,
  onResult: (result: SpApiOAuthResultMessage) => void,
): (() => void) | null {
  let channel: BroadcastChannel | undefined;
  try {
    const id = crypto.randomUUID();
    channel = new BroadcastChannel(`adpilot:sp-api:${id}`);
    popupStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ id, expiresAt: Date.now() + SP_API_OAUTH_TIMEOUT_MS }),
    );
    const receiver = channel;
    let received = false;
    receiver.onmessage = ({ data }: MessageEvent<unknown>) => {
      const parsed = spApiOAuthResultMessageSchema.safeParse(data);
      if (received || !parsed.success) return;
      received = true;
      receiver.postMessage(ACK);
      onResult(parsed.data);
    };
    return () => receiver.close();
  } catch {
    channel?.close();
    return null;
  }
}

/** Only close a detached popup once its initiating tab acknowledges the result. */
export async function sendSpApiOAuthResult(
  result: SpApiOAuthResultMessage,
  storage: RelayStorage,
): Promise<boolean> {
  let channel: BroadcastChannel;
  try {
    const value = storage.getItem(STORAGE_KEY);
    storage.removeItem(STORAGE_KEY);
    const relay = relaySchema.safeParse(value ? JSON.parse(value) : null);
    if (!relay.success || relay.data.expiresAt <= Date.now()) return false;
    channel = new BroadcastChannel(`adpilot:sp-api:${relay.data.id}`);
  } catch {
    return false;
  }
  return new Promise((resolve) => {
    const finish = (delivered: boolean) => {
      clearTimeout(timeout);
      channel.close();
      resolve(delivered);
    };
    const timeout = setTimeout(() => finish(false), 1500);
    channel.onmessage = ({ data }: MessageEvent<unknown>) => {
      if (data === ACK) finish(true);
    };
    channel.postMessage(result);
  });
}
